import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, RouterButtonLink } from "../components/Button";
import { Card } from "../components/Card";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Container } from "../components/Container";
import { FormActions, FormActionsStart } from "../components/FormActions";
import { InfoBox } from "../components/InfoBox";
import { LoadingContainer } from "../components/LoadingContainer";
import { PageDetailHeader } from "../components/PageDetailHeader";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { CATALOG_NEW_ITEM_ID, useCatalogNav } from "./catalog/catalogNav";
import { SupplierForm } from "./components/SupplierForm";
import {
  useGetSupplierQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useDeleteSupplierMutation,
} from "../query/suppliersQuery";
import type { RestCreateSupplier } from "@KassaroAB/shared/types/supplier";

const FORM_ID = "catalog-supplier-form";

export const SupplierFormView = () => {
  const { t } = useTranslation();
  const { setDirty, allowNavigate } = useCatalogNav();
  const { scope, pagePath } = useCatalogScope("suppliers");
  const { itemId } = useParams<{ itemId: string }>();
  const isCreating = itemId === CATALOG_NEW_ITEM_ID;
  const supplierId = !isCreating && itemId ? itemId : null;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const {
    data: supplier,
    isLoading,
    error: loadError,
  } = useGetSupplierQuery(scope, supplierId);
  const {
    mutate: createMutation,
    error: createError,
    isPending: isCreatePending,
  } = useCreateSupplierMutation(scope);
  const {
    mutate: updateMutation,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdateSupplierMutation(scope);
  const {
    mutate: deleteMutation,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeleteSupplierMutation(scope);

  const isSaving = isCreatePending || isUpdatePending;
  const missing = !isCreating && !!supplierId && !isLoading && !supplier;

  const handleSubmit = (data: RestCreateSupplier) => {
    if (isCreating) {
      createMutation(data, {
        onSuccess: (created) => {
          allowNavigate(`${pagePath}/${created.id}`);
        },
      });
      return;
    }
    if (!supplierId) return;
    updateMutation(
      { supplierId, supplier: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("suppliersView.successUpdated"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!supplierId) return;
    deleteMutation(supplierId, {
      onSuccess: () => {
        setDeleteOpen(false);
        allowNavigate(pagePath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const title = isCreating
    ? t("suppliersView.panelCreate")
    : (supplier?.name ?? t("suppliersView.title"));
  const breadcrumbCurrent = isCreating
    ? t("suppliersView.panelCreate")
    : missing
      ? t("catalog.itemNotFound")
      : (supplier?.name ?? t("common.loading"));

  if (!scope) {
    return null;
  }

  const showForm = isCreating || (!!supplier && !missing);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("suppliersView.title")}
        parentTo={pagePath}
        currentLabel={breadcrumbCurrent}
      />
      {loadError && (
        <InfoBox variant="error">{t("suppliersView.errorLoad")}</InfoBox>
      )}
      {(createError || updateError || deleteError) && (
        <InfoBox variant="error">{t("suppliersView.errorSave")}</InfoBox>
      )}
      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}
      <Card>
        {!isCreating && isLoading && !supplier ? (
          <LoadingContainer message={t("suppliersView.loading")} />
        ) : missing || !showForm ? (
          <InfoBox variant="error">{t("catalog.itemNotFound")}</InfoBox>
        ) : (
          <>
            <SupplierForm
              key={supplier?.id ?? CATALOG_NEW_ITEM_ID}
              formId={FORM_ID}
              supplier={isCreating ? undefined : supplier}
              savedTick={savedTick}
              onSubmit={handleSubmit}
              onDirtyChange={setDirty}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("common.delete")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink
                to={pagePath}
                $color="secondary"
                onClick={(event) => {
                  if (isSaving) event.preventDefault();
                }}
              >
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.update")}
              </Button>
            </FormActions>
          </>
        )}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("supplierForm.deleteSupplier")}
        message={t("suppliersView.deleteConfirm", {
          name: supplier?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
